import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ColorRing } from "react-loader-spinner";

const GameMobile = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [username, setUsername] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);
  const [playerId, setPlayerId] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [tempAnswer, setTempAnswer] = useState(null);
  const [isAnswerSubmitting, setIsAnswerSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    // Kiểm tra thông tin đăng ký từ localStorage
    const savedUsername = localStorage.getItem("game_username");
    const savedPlayerId = localStorage.getItem("game_player_id");
    if (savedUsername && savedPlayerId) {
      setUsername(savedUsername);
      setPlayerId(savedPlayerId);
      setIsRegistered(true);
    }

    const connectWebSocket = () => {
      // Sử dụng window.location.hostname thay vì localhost
      const wsUrl = `wss://wsgame.visgroup.co`;
      // const wsUrl = `ws://${window.location.hostname}:6789`;
      console.log("Connecting to:", wsUrl);
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log("Connected to game server!");
        setIsLoading(false);
        // Nếu đã có thông tin đăng ký, tự động kết nối lại
        if (savedUsername && savedPlayerId) {
          wsRef.current.send(JSON.stringify({
            type: "register",
            username: savedUsername,
            player_id: savedPlayerId
          }));
        }
      };

      wsRef.current.onerror = error => {
        console.error("WebSocket error:", error);
        toast.error("Lỗi kết nối. Vui lòng thử lại!");
        setIsLoading(false);
      };

      wsRef.current.onclose = () => {
        console.warn("WebSocket connection closed");
        toast.warn("Mất kết nối. Đang thử kết nối lại...");
        setIsLoading(true);
        
        // Thử kết nối lại sau 3 giây
        setTimeout(connectWebSocket, 3000);
      };

      wsRef.current.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          console.log("Received data:", data);

          if (data.type === "ping") {
            wsRef.current.send(JSON.stringify({ type: "pong" }));
            return;
          }

          if (data.type === "game_state") {
            // Luôn cập nhật trạng thái game
            setGameStarted(data.game_started);
            
            // Cập nhật câu hỏi nếu có thay đổi
            if (data.question && 
               (!currentQuestion || 
                data.question.id !== currentQuestion.id || 
                data.is_new_question)) {
              console.log("Updating question:", data.question);
              setCurrentQuestion(data.question);
              setTempAnswer(null);
              setSelectedAnswer(null);
              setIsAnswerSubmitting(false);
              setHasSubmitted(false);
            }

            // Reset state khi game kết thúc
            if (!data.game_started) {
              setCurrentQuestion(null);
              setTempAnswer(null);
              setSelectedAnswer(null);
              setIsAnswerSubmitting(false);
              setHasSubmitted(false);
            }
          } else if (data.type === "register_success") {
            setPlayerId(data.player_id);
            localStorage.setItem("game_username", username);
            localStorage.setItem("game_player_id", data.player_id);
            setIsRegistered(true);
            toast.success(data.message);
          }
        } catch (err) {
          console.error("Error parsing message:", err);
        }
      };
    };

    // Khởi tạo kết nối WebSocket
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const handleRegister = () => {
    if (!username.trim()) {
      toast.error("Vui lòng nhập tên của bạn!");
      return;
    }
    
    wsRef.current.send(JSON.stringify({ 
      type: "register",
      username: username
    }));
  };

  const handleSelectAnswer = (answer) => {
    if (!gameStarted || !currentQuestion || hasSubmitted || isAnswerSubmitting) {
      return;
    }
    setTempAnswer(answer);
  };

  const handleSubmitAnswer = async () => {
    if (!gameStarted || !currentQuestion || !playerId || !tempAnswer) {
      toast.error("Vui lòng chọn đáp án!");
      return;
    }

    if (isAnswerSubmitting || hasSubmitted) {
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error("Mất kết nối! Đang kết nối lại...");
      return;
    }

    try {
      setIsAnswerSubmitting(true);
      
      wsRef.current.send(JSON.stringify({
        type: "answer",
        answer: tempAnswer,
        player_id: playerId
      }));

      setSelectedAnswer(tempAnswer);
      setHasSubmitted(true);
      toast.success("Đã gửi câu trả lời!");

      setTimeout(() => {
        setIsAnswerSubmitting(false);
      }, 1000);

    } catch (error) {
      console.error("Error sending answer:", error);
      toast.error("Không thể gửi câu trả lời. Vui lòng thử lại!");
      setIsAnswerSubmitting(false);
    }
  };

  if (!isRegistered) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] text-white p-4 flex flex-col items-center justify-center w-full">
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-bold text-yellow-300 mb-8 text-center">Game Vui</h1>
          
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl mb-6 text-center">Đăng ký tham gia</h2>
            
            <div className="mb-6">
              <input
                type="text"
                placeholder="Nhập tên của bạn"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-yellow-500"
              />
            </div>
            
            <button
              onClick={handleRegister}
              className="w-full py-3 px-6 bg-yellow-500 text-black rounded-lg font-bold hover:bg-yellow-400 transition-colors"
            >
              Vào Game
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white p-4">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-yellow-300 mb-4">Game Vui</h1>
        <p className="text-gray-400">Xin chào, {username}!</p>
      </div>

      {/* Game Area */}
      {gameStarted ? (
        currentQuestion ? (
          <div className="flex flex-col items-center">
            {/* Answer Buttons */}
            <div className="grid grid-cols-2 gap-4 w-full max-w-md mb-6">
              {currentQuestion.answers.map((answer, index) => {
                const answerLetter = String.fromCharCode(65 + index);
                const isTemp = tempAnswer === answerLetter;
                const isSelected = selectedAnswer === answerLetter;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleSelectAnswer(answerLetter)}
                    disabled={hasSubmitted || isAnswerSubmitting}
                    className={`aspect-square flex items-center justify-center text-3xl font-bold rounded-2xl transition-colors ${
                      isSelected ? 'bg-blue-600 text-white' :
                      isTemp ? 'bg-yellow-600 text-white' :
                      'bg-gray-800 hover:bg-gray-700 text-yellow-500'
                    } ${(hasSubmitted || isAnswerSubmitting) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {answerLetter}
                  </button>
                );
              })}
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmitAnswer}
              disabled={!tempAnswer || hasSubmitted || isAnswerSubmitting}
              className={`px-8 py-4 rounded-xl font-bold text-xl w-full max-w-md transition-colors ${
                !tempAnswer || hasSubmitted || isAnswerSubmitting
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-yellow-500 hover:bg-yellow-400 text-black'
              }`}
            >
              {isAnswerSubmitting ? 'Đang gửi...' : 
               hasSubmitted ? 'Đã gửi đáp án' : 
               tempAnswer ? `Gửi đáp án ${tempAnswer}` : 
               'Chọn đáp án'}
            </button>

            {hasSubmitted && (
              <div className="mt-6 text-center">
                <div className="text-blue-500 font-bold text-xl">
                  Đã gửi đáp án {selectedAnswer}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-6 bg-gray-800 rounded-lg">
            <p className="text-gray-400">Đang chờ câu hỏi tiếp theo...</p>
          </div>
        )
      ) : (
        <div className="text-center p-6 bg-gray-800 rounded-lg">
          <p className="text-gray-400">Đang chờ người điều khiển bắt đầu game...</p>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <ColorRing
            height={70}
            width={70}
            color="#4fa94d"
            ariaLabel="loading-indicator"
          />
        </div>
      )}

      <ToastContainer 
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default GameMobile; 